<mat-card *authGroup="['SuperAdmin', 'OperationsManager']" class="user-view">
  @if (user) {
    <div class="row">
      <div class="col-md-6">
        <mat-card-title>
          {{getAttribute('name', user)}}
        </mat-card-title>
      </div>
      @if (perm.canMutate()) {
        <div class="col-md-6 text-end">
          <button mat-stroked-button color="primary" class="me-2" (click)="resetPassword()">
            Reset password
          </button>
          <button (click)="toggleEditMode()" mat-flat-button color="primary">
            {{editMode ? 'Details' : 'Edit'}}
          </button>
        </div>
      }
    </div>
  }
  <p class="mt-2">
    <button mat-stroked-button (click)="backToList()" class="me-2">
      <mat-icon>arrow_back</mat-icon> Back to list
    </button>
  </p>
  @if (user && !editMode) {
    <div class="details mt-3">
      <table>
        <tr>
          <th>
            Email
          </th>
          <td>
            {{getAttribute('email', user)}}
          </td>
        </tr>
        <tr>
          <th>
            Phone number
          </th>
          <td>
            {{getAttribute('phone_number', user)}}
          </td>
        </tr>
        <tr>
          <th>
            Role
          </th>
          <td>
            <mat-chip-set>
              @for (role of currentRoles; track role) {
                <mat-chip>
                  {{role}}
                </mat-chip>
              }
            </mat-chip-set>
          </td>
        </tr>
        <tr>
          <th>
            Company / Individual
          </th>
          <td>
            <mat-chip-set>
              @for (company of companies; track company) {
                <mat-chip>
                  {{company}}
                </mat-chip>
              }
            </mat-chip-set>
          </td>
        </tr>
      </table>
    </div>
  }
  @if (editMode) {
    <form [formGroup]="updateUserForm" class="mt-3" (ngSubmit)="save()">
      <div class="row">
        <div class="col-md-6">
          <p>
            <mat-form-field appearance="outline">
              <mat-label>Name</mat-label>
              <input formControlName="name" type="text" matInput placeholder="Name">
              @if (updateUserForm.get('name')?.invalid) {
                <mat-error>
                  Name is required
                </mat-error>
              }
            </mat-form-field>
          </p>
          <p>
            <mat-form-field appearance="outline">
              <mat-label>Email</mat-label>
              <input readonly formControlName="email" type="email" matInput
                placeholder="Email">
              @if (updateUserForm.get('email')?.invalid) {
                <mat-error>
                  Email is required
                </mat-error>
              }
            </mat-form-field>
          </p>
          <p>
            <mat-form-field appearance="outline" class="company-individual-field">
              <mat-label>Company or individual</mat-label>
              <div class="company-individual-row">
                <mat-button-toggle-group name="linkType" [value]="linkType" (change)="setLinkType($event.value)">
                  <mat-button-toggle value="company">Company</mat-button-toggle>
                  <mat-button-toggle value="individual">Individual</mat-button-toggle>
                </mat-button-toggle-group>
                <mat-chip-grid #companyList aria-label="Company or individual selection" class="company-individual-search">
                  @for (company of companies; track company) {
                    <mat-chip-row [removable]="removable" (removed)="removeCompany(company)">
                      {{company}}
                      @if (removable) {
                        <button matChipRemove>
                          <mat-icon>cancel</mat-icon>
                        </button>
                      }
                    </mat-chip-row>
                  }
                  @if (linkType === 'company') {
                    <input #companyInput
                      [placeholder]="companies.length > 0 ? '' : 'Search companies...'"
                      formControlName="companySearch"
                      [matAutocomplete]="companyAuto"
                      [matChipInputFor]="companyList"
                      [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                      (matChipInputTokenEnd)="addCompany($event)">
                  }
                  @if (linkType === 'individual') {
                    <input #individualInput
                      [placeholder]="companies.length > 0 ? '' : 'Search individuals...'"
                      formControlName="individualSearch"
                      [matAutocomplete]="individualAuto"
                      [matChipInputFor]="companyList"
                      [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                      (matChipInputTokenEnd)="addCompany($event)">
                  }
                </mat-chip-grid>
              </div>
              <mat-autocomplete #companyAuto="matAutocomplete" (optionSelected)="selectedCompany($event)">
                @for (company of filteredCompanies | async; track company) {
                  <mat-option [value]="company">
                    {{ company.name }}
                  </mat-option>
                }
              </mat-autocomplete>
              <mat-autocomplete #individualAuto="matAutocomplete" (optionSelected)="selectedIndividual($event)">
                @for (ind of filteredIndividuals | async; track ind) {
                  <mat-option [value]="ind">
                    {{ (ind.title ? ind.title + ' ' : '') + ind.first_name + ' ' + ind.last_name }} ({{ getIndividualTypeLabel(ind) }})
                  </mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
          </p>
        </div>
        <div class="col-md-6">
          <p>
            <mat-form-field appearance="outline">
              <mat-label>Phone number</mat-label>
              <input readonly formControlName="phone_number" type="text" matInput
                placeholder="Phone number">
              @if (updateUserForm.get('phone_number')?.invalid) {
                <mat-error>
                  Phone number
                </mat-error>
              }
            </mat-form-field>
          </p>
          <p>
            <mat-form-field appearance="outline">
              <mat-label>Roles</mat-label>
              <mat-chip-grid #roleList aria-label="Role selection">
                @for (role of roles; track role) {
                  <mat-chip-row [removable]="removable"
                    (removed)="remove(role)">
                    {{role}}
                    @if (removable) {
                      <button matChipRemove>
                        <mat-icon>cancel</mat-icon>
                      </button>
                    }
                  </mat-chip-row>
                }
                <input #roleInput placeholder="Add role..." formControlName="roles" [matAutocomplete]="auto"
                  [matChipInputFor]="roleList" [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                  (matChipInputTokenEnd)="add($event)">
              </mat-chip-grid>
            </mat-form-field>
            <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
              @for (role of filteredRoles | async; track role) {
                <mat-option [value]="role">
                  {{role}}
                </mat-option>
              }
            </mat-autocomplete>
          </p>
        </div>
      </div>
      <div class="text-end mt-2">
        <button mat-raised-button color="primary">
          <mat-icon>save</mat-icon> Save
        </button>
      </div>
    </form>
  }
</mat-card>